import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Alert, Card } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LoginCredentials } from '../../../types';
import { loginUser, clearError } from '../../../store/slices/authSlice';
import { RootState, AppDispatch } from '../../../store';
import { LoginContainer, LoginCard, Logo, Title, Subtitle } from './LoginForm.styles';

const LoginForm: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  // State for MFA flow
  const [showMFA, setShowMFA] = useState(false);
  const [credentials, setCredentials] = useState<LoginCredentials | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
      setMfaError(null);
    };
  }, [dispatch]);

  const onFinishInitialLogin = async (values: LoginCredentials) => {
    setMfaError(null);
    setCredentials(values);
    
    const result = await dispatch(loginUser(values));
    
    console.log('Login result:', result);
    
    if (result.type === 'auth/login/fulfilled') {
      navigate('/dashboard');
    } else if (result.type === 'auth/login/rejected') {
      const payload = result.payload as any;
      
      console.log('Login rejected payload:', payload);
      
      // Check if MFA is required
      if (payload?.mfa_required) {
        console.log('MFA required, showing MFA form');
        setShowMFA(true);
      }
    }
  };

  const onFinishMFA = async (values: { otp_token?: string; backup_token?: string }) => {
    if (!credentials) return;
    
    setMfaError(null);
    
    const mfaCredentials = {
      ...credentials,
      ...values
    };
    
    const result = await dispatch(loginUser(mfaCredentials));
    
    if (result.type === 'auth/login/fulfilled') {
      navigate('/dashboard');
    } else {
      setMfaError('Code de vérification invalide. Veuillez réessayer.');
    }
  };

  const resetToLogin = () => {
    setShowMFA(false);
    setCredentials(null);
    setMfaError(null);
    form.resetFields();
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <img 
            src="/logo-tinsa.jpg" 
            alt="Tinsa by Accumin" 
            style={{ 
              height: '60px', 
              width: 'auto',
              objectFit: 'contain' 
            }}
          />
        </Logo>
        
        <Title>TinsaFlow</Title>
        <Subtitle>
          {showMFA 
            ? 'Entrez votre code d\'authentification' 
            : 'Connectez-vous à votre espace d\'expertise'
          }
        </Subtitle>

        {/* Only show error if it's a string and not during MFA flow */}
        {!showMFA && error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {/* Show MFA-specific errors */}
        {showMFA && mfaError && (
          <Alert
            message={mfaError}
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {!showMFA ? (
          // Initial login form (email/password)
          <Form
            form={form}
            name="login"
            onFinish={onFinishInitialLogin}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Veuillez saisir votre email!' },
                { type: 'email', message: 'Format email invalide!' }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Votre email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Mot de passe"
              rules={[{ required: true, message: 'Veuillez saisir votre mot de passe!' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Votre mot de passe"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                Se connecter
              </Button>
            </Form.Item>
          </Form>
        ) : (
          // MFA form (OTP/backup token)
          <Form
            name="mfa"
            onFinish={onFinishMFA}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="otp_token"
              label="Code d'authentification (6 chiffres)"
              rules={[
                {
                  pattern: /^\d{6}$/,
                  message: 'Le code doit contenir exactement 6 chiffres'
                }
              ]}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="123456"
                maxLength={6}
                autoComplete="one-time-code"
              />
            </Form.Item>

            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <span style={{ color: '#8c8c8c', fontSize: '12px' }}>ou</span>
            </div>

            <Form.Item
              name="backup_token"
              label="Code de récupération"
              rules={[
                {
                  pattern: /^[A-Za-z0-9]{8}$/,
                  message: 'Le code de récupération doit contenir 8 caractères'
                }
              ]}
            >
              <Input
                placeholder="Code de récupération"
                maxLength={8}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                Vérifier
              </Button>
            </Form.Item>

            <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
              <Button
                type="link"
                onClick={resetToLogin}
                style={{ padding: 0 }}
              >
                ← Retour à la connexion
              </Button>
            </Form.Item>
          </Form>
        )}
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginForm;